/** Purpose-specific anonymous product analytics. Never authenticates or meters. */
export type AnalyticsEnvironment = "development" | "staging" | "production";
export type AnalyticsSurface = "landing_page" | "docs_site" | "react_web";
export type AnalyticsAuthState = "anonymous" | "authenticated" | "loading" | "unknown";
export type AnalyticsLocale = "en" | "es" | "pt-BR" | "not_applicable";
export type CookieScope = {
    environment: AnalyticsEnvironment;
    secure: boolean;
    domain?: string;
    hosts: readonly string[];
    identityMaxAgeSeconds: number;
    identityPolicyApproved: boolean;
};
export type AnonymousConfig = {
    environment: AnalyticsEnvironment;
    host: "https://us.i.posthog.com" | "https://eu.i.posthog.com";
    projectToken: string;
    deploySha: string;
    scope: CookieScope;
};
export const anonymousPageViewEvent = "public_site.page.viewed.v2";
export const analyticsConsentChanged = "dronejob:analytics-consent-changed";
const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/;
function retainedIdentity(value: string | null, environment: AnalyticsEnvironment): string | null {
    if (!value) return null;
    const parts = value.split(":");
    if (parts.length !== 3 || parts[0] !== environment || !uuid.test(parts[1])
        || !/^\d{13}$/.test(parts[2]) || Number(parts[2]) <= Date.now()) return null;
    return `abv2:${environment}:${parts[1]}`;
}
export const consentName = (environment: AnalyticsEnvironment) => `dj_analytics_consent_v2_${environment}`;
export const identityName = (environment: AnalyticsEnvironment) => `dj_analytics_browser_v2_${environment}`;
export function cookieValue(header: string, name: string): string | null {
    const values = header.split(";").flatMap(p => { const [n, ...v] = p.trim().split("="); return n === name ? [v.join("=")] : []; });
    return values.length === 1 ? values[0] : null;
}
export function analyticsConsent(header: string, environment: AnalyticsEnvironment): "granted" | "denied" | "unset" {
    const value = cookieValue(header, consentName(environment));
    return value === "granted" || value === "denied" ? value : "unset";
}
export function anonymousAuthState(state: AnalyticsAuthState, cookies: string): AnalyticsAuthState {
    if (state !== "anonymous")
        return state;
    const hints = cookies.split(";").some(part => { const [name, value] = part.trim().split("="); return /^dronejob_viewer_authenticated(?:_[a-f0-9]{32})?$/.test(name) && ["1", "true"].includes(value); });
    return hints ? "unknown" : "anonymous";
}
export function resolveCookieScope(input: {
    environment?: string;
    hostname: string;
    protocol: string;
    approvedHosts?: string;
    approvedParentDomain?: string;
    trustApproved?: string;
    identityMaxAgeSeconds?: string;
    identityPolicyApproved?: string;
}): CookieScope | null {
    if (!["development", "staging", "production"].includes(input.environment ?? ""))
        return null;
    const environment = input.environment as AnalyticsEnvironment;
    const hostname = input.hostname.toLowerCase();
    const hosts = (input.approvedHosts ?? "").split(",").map(v => v.trim().toLowerCase()).filter(Boolean);
    const selectedLoopback = /^127\.0\.0\.(?:[1-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-4])$/.test(hostname) && hosts.includes(hostname) && input.protocol === "https:";
    const local = environment === "development" && ((hostname === "localhost" && ["http:", "https:"].includes(input.protocol)) || selectedLoopback);
    const domain = input.approvedParentDomain?.replace(/^\./, "").toLowerCase();
    if (!local && (input.protocol !== "https:" || input.trustApproved !== "true" || hosts.length !== 3 || new Set(hosts).size !== 3 || !hosts.includes(hostname) || !domain || hosts.some(h => !h.endsWith("." + domain) && h !== domain) || !domain.includes(".") || domain.includes(":") || domain.includes("/")))
        return null;
    const age = Number(input.identityMaxAgeSeconds);
    const identityPolicyApproved = input.identityPolicyApproved === "true" && Number.isSafeInteger(age) && age >= 60 && age <= 15552000;
    return { environment, secure: input.protocol === "https:", ...(local ? {} : { domain }), hosts: local ? [hostname] : hosts, identityMaxAgeSeconds: identityPolicyApproved ? age : 0, identityPolicyApproved };
}
export function resolveAnonymousConfig(input: {
    enabled?: string;
    environment?: string;
    host?: string;
    projectToken?: string;
    deploySha?: string;
    projectId?: string;
    projectMode?: string;
    productionBindingApproved?: string;
    oldTokenRevoked?: string;
    nonProductionRetired?: string;
}, scope: CookieScope | null): AnonymousConfig | null {
    if (input.enabled !== "true" || !scope?.identityPolicyApproved || input.environment !== scope.environment || !["https://us.i.posthog.com", "https://eu.i.posthog.com"].includes(input.host ?? "") || !/^phc_[A-Za-z0-9_-]{16,200}$/.test(input.projectToken ?? "") || !/^(?!0{40}$)[a-f0-9]{40}$/.test(input.deploySha ?? ""))
        return null;
    if (input.nonProductionRetired !== undefined && !["true", "false"].includes(input.nonProductionRetired)) return null;
    if (scope.environment !== "production" && input.nonProductionRetired === "true") return null;
    if (scope.environment === "production" && (input.projectId !== "596116" || input.projectMode !== "promoted"
        || input.productionBindingApproved !== "true" || input.nonProductionRetired !== "true" || input.oldTokenRevoked !== "true"
        || input.host !== "https://us.i.posthog.com" || !scope.secure || !scope.domain)) return null;
    return { environment: scope.environment, scope, host: input.host as AnonymousConfig["host"], projectToken: input.projectToken!, deploySha: input.deploySha! };
}
function cookie(name: string, value: string, scope: CookieScope, age: number) { return `${name}=${value}; Path=/; Max-Age=${age}; SameSite=Lax${scope.domain ? `; Domain=${scope.domain}` : ""}${scope.secure ? "; Secure" : ""}`; }
export function chooseAnalyticsConsent(choice: "granted" | "denied", scope: CookieScope, io: {
    write(value: string): void;
    storage?: Pick<Storage, "removeItem">;
}) {
    // Remove conflicting legacy host cookies. No historical identify/alias.
    io.write(cookie(consentName(scope.environment), "", { ...scope, domain: undefined }, 0));
    io.write(cookie(consentName(scope.environment), choice, scope, 15552000));
    if (choice === "denied")
        clearAnalyticsIdentity(scope, io);
}
export function clearAnalyticsIdentity(scope: CookieScope, io: {
    write(value: string): void;
    storage?: Pick<Storage, "removeItem">;
}) {
    io.write(cookie(identityName(scope.environment), "", scope, 0));
    if (scope.domain)
        io.write(cookie(identityName(scope.environment), "", { ...scope, domain: undefined }, 0));
    try {
        io.storage?.removeItem("dj:posthog-product-anonymous-visitor-v1");
    }
    catch { }
}
export type AnalyticsBrowser = {
    read(): string;
    write(value: string): void;
    hostname: string;
    crypto: Pick<Crypto, "randomUUID">;
    storage?: Pick<Storage, "removeItem">;
    pause(ms: number): Promise<void>;
};
export async function resolveBrowserIdentity(scope: CookieScope, browser: AnalyticsBrowser, signal?: AbortSignal): Promise<string | null> {
    if (!scope.identityPolicyApproved || !scope.hosts.includes(browser.hostname) || analyticsConsent(browser.read(), scope.environment) !== "granted" || signal?.aborted)
        return null;
    const name = identityName(scope.environment);
    // Conflicting host/domain cookies must not be overwritten into a new
    // identity. Require explicit consent repair rather than silently respawning.
    const occurrences = browser.read().split(";").filter(part => part.trim().split("=", 1)[0] === name);
    if (occurrences.length > 1 || (occurrences.length === 1 && !cookieValue(browser.read(), name)))
        return null;
    let current = cookieValue(browser.read(), name);
    if (current) {
        if (!retainedIdentity(current, scope.environment)) return null;
    }
    else {
        const id = browser.crypto.randomUUID();
        if (!uuid.test(id))
            return null;
        browser.write(cookie(name, `${scope.environment}:${id}:${Date.now() + scope.identityMaxAgeSeconds * 1000}`, scope, scope.identityMaxAgeSeconds));
        // Cookie delivery is authoritative; no localStorage fallback when blocked.
        // Settle simultaneous first visits, then re-read again immediately at send.
        await browser.pause(250);
        current = cookieValue(browser.read(), name);
        if (!current)
            return null;
    }
    if (signal?.aborted || analyticsConsent(browser.read(), scope.environment) !== "granted")
        return null;
    const retained = retainedIdentity(current, scope.environment);
    if (!retained) return null;
    try {
        browser.storage?.removeItem("dj:posthog-product-anonymous-visitor-v1");
    }
    catch { }
    return retained;
}
export type AnonymousView = {
    surface: AnalyticsSurface;
    routeKey: string;
    locale: AnalyticsLocale;
    insertId: string;
};
const routes: Record<AnalyticsSurface, readonly string[]> = { landing_page: ["home", "public_directory", "public_profile", "marketing", "legal"], docs_site: ["public_docs"], react_web: ["public_directory", "login", "signup"] };
export function publicViewDimensions(surface: AnalyticsSurface, path: string, publicEligible = true): Omit<AnonymousView, "insertId"> | null {
    if (!publicEligible || typeof path !== "string" || path.length > 4096)
        return null;
    const clean = path.replace(/^#/, "").split(/[?#]/, 1)[0];
    const segments = clean.split("/").filter(Boolean);
    const locale: AnalyticsLocale = ["en", "es", "pt-BR"].includes(segments[0]) ? segments.shift() as AnalyticsLocale : "not_applicable";
    let routeKey: string | null = null;
    if (surface === "docs_site")
        routeKey = segments[0] === "docs" && segments[1] === "public" ? "public_docs" : null;
    if (surface === "react_web")
        routeKey = ["login", "signup"].includes(segments[0]) ? segments[0] : segments[0] === "directory" ? "public_directory" : null;
    if (surface === "landing_page")
        routeKey = !segments.length ? "home" : segments[0] === "p" ? "public_profile" : ["terms", "privacy", "cookie-policy", "acceptable-use"].includes(segments[0]) ? "legal" : ["pilots", "missions"].includes(segments[0]) ? "public_directory" : ["enterprise", "help", "memberships", "partners", "platform", "resources", "solutions"].includes(segments[0]) ? "marketing" : null;
    return routeKey ? { surface, routeKey, locale } : null;
}
export async function captureAnonymousView(input: {
    config: AnonymousConfig | null;
    view: AnonymousView;
    auth: () => AnalyticsAuthState;
    browser: AnalyticsBrowser;
    fetcher?: typeof fetch;
    signal?: AbortSignal;
}): Promise<boolean> {
    const { config, view, browser } = input;
    if (!config || input.auth() !== "anonymous" || !routes[view.surface]?.includes(view.routeKey) || !["en", "es", "pt-BR", "not_applicable"].includes(view.locale) || !/^av2:[a-f0-9-]{36}$/.test(view.insertId))
        return false;
    const id = await resolveBrowserIdentity(config.scope, browser, input.signal);
    if (!id || input.auth() !== "anonymous" || input.signal?.aborted || analyticsConsent(browser.read(), config.environment) !== "granted")
        return false;
    // Use the shared cookie's latest value if another first visit won initialization.
    const current = cookieValue(browser.read(), identityName(config.environment));
    const latestIdentity = retainedIdentity(current, config.environment);
    if (!latestIdentity) return false;
    try {
        const result = await (input.fetcher ?? fetch)(config.host + "/capture/", { method: "POST", credentials: "omit", referrerPolicy: "no-referrer", signal: input.signal, headers: { "content-type": "application/json" }, body: JSON.stringify({ api_key: config.projectToken, event: anonymousPageViewEvent, properties: { distinct_id: latestIdentity, $insert_id: view.insertId, $process_person_profile: false, $geoip_disable: true, event_version: "v2", environment: config.environment, data_origin: "application", deploy_sha: config.deploySha, surface: view.surface, route_key: view.routeKey, locale: view.locale, auth_state: "anonymous" } }) });
        return result.ok && analyticsConsent(browser.read(), config.environment) === "granted" && input.auth() === "anonymous";
    }
    catch {
        return false;
    }
}

/** Deployment-owned legal origin; no browser-provided return URL. */
export function analyticsLegalHref(landingUrl: string | undefined, locale: string, path: "privacy" | "cookie-policy"): string {
    const language = ["en", "es", "pt-BR"].includes(locale) ? locale : "en";
    try {
        const u = new URL(landingUrl ?? "https://dronejob.ai");
        const local = u.hostname === "localhost" || /^127\.0\.0\.(?:[1-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-4])$/.test(u.hostname);
        if (u.username || u.password || u.search || u.hash || u.pathname !== "/" || (u.protocol !== "https:" && !(local && u.protocol === "http:"))) throw new Error("invalid_origin");
        return `${u.origin}/${language}/${path}`;
    } catch { return `https://dronejob.ai/${language}/${path}`; }
}
